package io.github.dautovicharis.charts.app

import androidx.lifecycle.ViewModel
import io.github.dautovicharis.charts.app.ui.theme.Theme
import io.github.dautovicharis.charts.app.ui.theme.blueViolet
import io.github.dautovicharis.charts.app.ui.theme.citrusGrove
import io.github.dautovicharis.charts.app.ui.theme.deepOceanBlue
import io.github.dautovicharis.charts.app.ui.theme.deepRed
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update

data class MenuState(
    val menuItems: List<ChartDestination>,
    val selectedSubmenu: ChartSubmenuItem? = null,
)

enum class DarkModeSettings {
    System,
    On,
    Off,
}

data class ThemesState(
    val themes: List<Theme>,
    val selectedTheme: Theme,
    val darkMode: DarkModeSettings,
    val useDynamicColors: Boolean,
)

class MainViewModel : ViewModel() {
    private val _menuState =
        MutableStateFlow(
            MenuState(
                listOf(
                    ChartDestination.PieChartScreen,
                    ChartDestination.LineChartScreen,
                    ChartDestination.MultiLineChartScreen,
                    ChartDestination.BarChartScreen,
                    ChartDestination.RadarChartScreen,
                ),
            ),
        )

    val menuState: StateFlow<MenuState> = _menuState.asStateFlow()

    private val _themeState =
        MutableStateFlow(
            ThemesState(
                themes =
                    listOf(
                        deepRed,
                        blueViolet,
                        deepOceanBlue,
                        citrusGrove,
                    ),
                selectedTheme = deepRed,
                darkMode = DarkModeSettings.System,
                useDynamicColors = false,
            ),
        )

    val themeState: StateFlow<ThemesState> = _themeState.asStateFlow()

    fun onThemeSelected(newTheme: Theme) {
        _themeState.update {
            it.copy(
                selectedTheme = newTheme,
            )
        }
    }

    fun toggleDarkMode() {
        _themeState.update {
            val newDarkMode =
                when (it.darkMode) {
                    DarkModeSettings.System -> DarkModeSettings.Off
                    DarkModeSettings.Off -> DarkModeSettings.On
                    DarkModeSettings.On -> DarkModeSettings.System
                }
            it.copy(
                darkMode = newDarkMode,
            )
        }
    }

    fun toggleDynamicColor() {
        _themeState.update {
            it.copy(
                useDynamicColors = !it.useDynamicColors,
            )
        }
    }

    fun onSubmenuSelected(submenu: ChartSubmenuItem) {
        _menuState.update {
            it.copy(selectedSubmenu = submenu)
        }
    }

    fun onSubmenuUnselected() {
        _menuState.update {
            it.copy(selectedSubmenu = null)
        }
    }

    fun resolveDarkTheme(isSystemInDark: Boolean): Boolean {
        return when (themeState.value.darkMode) {
            DarkModeSettings.System -> isSystemInDark
            DarkModeSettings.On -> true
            DarkModeSettings.Off -> false
        }
    }
}
